import { useLocation } from "wouter";
import { DaySummary } from "./DaySummary";
import type { CompassDir, ScoredDay, SpeedUnit } from "../lib/types";
import styles from "./ForecastCol.module.css";

interface ForecastColProps {
  day: ScoredDay;
  spotId: string;
  unit: SpeedUnit;
  /** Spot's ideal directions — passed through to DaySummary so the arrow
   *  accent reflects direction match (not the combined level/score). */
  idealDirs: readonly CompassDir[];
  /** Override the navigation target for this column. Receives the day
   *  offset and returns a path. Defaults to `/spot/{spotId}?day=N`. The
   *  custom-location card on the spots map uses this to send the user
   *  to `/point/{lat}/{lon}?day=N` instead. */
  hrefFor?: (dayOffset: number) => string;
  /** Day strip's selected day (overview page). When set, the matching
   *  column renders in the selected variant (body-2 + foreground-strong)
   *  and the rest dim to 70% opacity. `undefined` disables both states
   *  entirely (the column renders default). */
  selectedDay?: number | undefined;
}

/**
 * One day-column inside a SpotCard's forecast row. Wraps a DaySummary
 * with a button that navigates to the spot detail with that day
 * pre-selected via `?day=N`.
 */
export function ForecastCol({
  day,
  spotId,
  unit,
  idealDirs,
  hrefFor,
  selectedDay,
}: ForecastColProps) {
  const [, setLocation] = useLocation();

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const path = hrefFor
      ? hrefFor(day.dayOffset)
      : `/spot/${spotId}?day=${day.dayOffset}`;
    setLocation(path);
  };

  const isSelected = selectedDay !== undefined && day.dayOffset === selectedDay;
  const isDimmed = selectedDay !== undefined && !isSelected;

  return (
    <button
      type="button"
      className={[styles.forecastCol, isDimmed && styles.forecastColDimmed]
        .filter(Boolean)
        .join(" ")}
      data-day={day.dayOffset}
      data-spot-id={spotId}
      onClick={onClick}
    >
      <DaySummary
        day={day}
        unit={unit}
        idealDirs={idealDirs}
        selected={isSelected}
      />
    </button>
  );
}
